import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';

/**
 * RecentlyViewed Component
 * Shows products the user has recently viewed (stored in localStorage)
 */
const RecentlyViewed = ({ currentProductId = null, maxItems = 4 }) => {
  // Lazy initializer reads localStorage once on mount — no effect needed.
  const [storedProducts] = useState(() => {
    try {
      const stored = localStorage.getItem('recently_viewed');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error parsing recently viewed:', e);
      return [];
    }
  });

  // Filter and slice in useMemo to avoid setting state in effect
  const recentProducts = useMemo(() => {
    let products = storedProducts;
    if (currentProductId) {
      products = products.filter(p => p.id !== currentProductId);
    }
    return products.slice(0, maxItems);
  }, [storedProducts, currentProductId, maxItems]);

  if (recentProducts.length === 0) return null;

  return (
    <section className="py-10 bg-[var(--bg-soft)]" data-testid="recently-viewed">
      <div className="wrap">
        <div className="flex items-center gap-2 mb-6">
          <Clock className="h-5 w-5 text-[var(--brand)]" />
          <h2 className="text-xl font-bold text-[var(--text)]">Recently Viewed</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {recentProducts.map((product) => (
            <Link
              key={product.id}
              to={`/product/${product.slug}`}
              className="group bg-white rounded-xl overflow-hidden border border-[var(--border)] hover:shadow-md transition-shadow"
              data-testid={`recently-viewed-${product.id}`}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-3">
                <h3 className="text-sm font-medium text-[var(--text)] line-clamp-1 group-hover:text-[var(--brand)] transition-colors">
                  {product.name}
                </h3>
                <p className="text-sm font-bold text-[var(--brand)] mt-1">
                  ${product.price}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Helper function to add a product to recently viewed
 * Call this when a product page loads
 */
export const addToRecentlyViewed = (product) => {
  if (!product || !product.id) return;
  
  const MAX_ITEMS = 10;
  const stored = localStorage.getItem('recently_viewed');
  let products = [];
  
  if (stored) {
    try {
      products = JSON.parse(stored);
    } catch (e) {
      products = [];
    }
  }
  
  // Remove if already exists
  products = products.filter(p => p.id !== product.id);
  
  // Add to beginning
  products.unshift({
    id: product.id,
    slug: product.slug,
    name: product.name,
    price: product.price,
    image: product.image
  });
  
  // Keep only last MAX_ITEMS
  products = products.slice(0, MAX_ITEMS);
  
  localStorage.setItem('recently_viewed', JSON.stringify(products));
};

export default RecentlyViewed;
